import cloneDeep from 'clone-deep'
import { ProductTypes } from 'config'
import { getMappedProductType } from 'config/loan.constants'
import { ILoan, ILoanDetail, IMultipleProperty } from 'pages/LoanStructure/interfaces'
import { analyzeLoanStructureValue, calcInitialLoanAmount, isNonDSCRProduct } from 'pages/LoanStructure/Logic'
import { store } from 'reducers'
import { getLoanTerm, getPrice2decimal, removeComma } from 'utils'
import { getInterestOrNot } from 'utils/getInterestOrNot'

export function combinedMonthlyExpense(loan: ILoan, rateData: any) {
  const interest_rate = removeComma(rateData.rate)
  const pricingType = rateData.type
  const productTypeForRules = getMappedProductType(loan.productType)

  const {
    proposedMonthlyInsurance,
    proposedMonthlyTaxes,
    proposedMonthlyHoaDues,
    proposedMonthlyRent,
    floodInsurance,
    schoolTax,
    otherTax,
    secondLienPayment,
    rehabBudget,
  } = analyzeLoanStructureValue(loan)
  let proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  if (
    [ProductTypes.FixAndFlip, ProductTypes.GroundUpConstruction, ProductTypes.Bridge].includes(
      productTypeForRules as ProductTypes,
    ) &&
    loan.interestType === 'Non Dutch'
  )
    proposedLoanAmount = removeComma(calcInitialLoanAmount(loan, true))

  const monthsReserve = removeComma(loan.monthsReserve)

  let mortgage_payment = (proposedLoanAmount * interest_rate) / 100
  mortgage_payment = mortgage_payment / 12
  let c_text1 = 'Monthly Interest Payment'

  // Calculate Principal and Interest payment for DSCR products
  if (!isNonDSCRProduct(loan.productType)) {
    const state = store.getState()
    const loanDetail = state.loanDetail as ILoanDetail
    const pricingEngineIsOff = !loanDetail?.pricingEngine
    const IsInterestOnly = loan?.amortizationType?.includes('IO')
    const shouldCalculatePrincipalAndInterest = pricingEngineIsOff
      ? !IsInterestOnly
      : !!pricingType && !getInterestOrNot(pricingType)

    if (shouldCalculatePrincipalAndInterest) {
      c_text1 = 'Monthly Principal and Interest'
      const loanTerm = getLoanTerm(pricingType, loan.loanTerm)
      const interest = interest_rate / 100
      const value = Math.pow(1 + interest / 12, loanTerm)
      mortgage_payment = Number((((proposedLoanAmount * interest) / 12) * value) / (value - 1))
    }
  }

  let Rlt: any = {
    title: c_text1,
    mortgagePayment: getPrice2decimal(Number(mortgage_payment.toFixed(2))),
    proposedMonthlyInsurance: getPrice2decimal(proposedMonthlyInsurance),
    proposedMonthlyTaxes: getPrice2decimal(proposedMonthlyTaxes),
    proposedMonthlyHoaDues: getPrice2decimal(proposedMonthlyHoaDues),
    proposedMonthlyRent: getPrice2decimal(proposedMonthlyRent),
    secondLienPayment: getPrice2decimal(secondLienPayment),
    floodInsurance: getPrice2decimal(floodInsurance),
    schoolTax: getPrice2decimal(schoolTax),
    otherTax: getPrice2decimal(otherTax),
    rehabBudget: getPrice2decimal(rehabBudget),
  }

  const sum =
    removeComma(Rlt.mortgagePayment) +
    removeComma(Rlt.proposedMonthlyInsurance) +
    removeComma(Rlt.proposedMonthlyTaxes) +
    removeComma(Rlt.proposedMonthlyHoaDues) +
    removeComma(Rlt.secondLienPayment) +
    removeComma(Rlt.floodInsurance) +
    removeComma(Rlt.schoolTax) +
    removeComma(Rlt.otherTax)
  const fullMonthlyPayment =
    removeComma(Rlt.mortgagePayment) +
    removeComma(Rlt.proposedMonthlyInsurance) +
    removeComma(Rlt.proposedMonthlyTaxes) +
    removeComma(Rlt.proposedMonthlyHoaDues) +
    removeComma(Rlt.floodInsurance) +
    removeComma(Rlt.schoolTax) +
    removeComma(Rlt.otherTax)

  Rlt.sum = getPrice2decimal(sum)
  Rlt.fullMonthlyPayment = getPrice2decimal(fullMonthlyPayment)

  const rtlTraderPercent = 0 / 100
  Rlt.requiredReserves = getPrice2decimal((Number(sum) * monthsReserve + rehabBudget * rtlTraderPercent).toFixed(2))

  let dscr: any = null
  if (interest_rate > 0) {
    dscr = removeComma(proposedMonthlyRent) / removeComma(sum)
    dscr = getPrice2decimal(dscr)
  }
  Rlt.dscr = dscr
  return Rlt
}

export function combinedPropertyMonthlyExpense(loan: ILoan, propertyData: IMultipleProperty, rateData: any) {
  const interest_rate = removeComma(rateData.rate)
  const pricingType = rateData.type
  const productTypeForRules = getMappedProductType(loan.productType)

  const rehabBudget = removeComma(loan.rehabBudget)
  const monthsReserve = removeComma(loan.monthsReserve)
  const proposedMonthlyInsurance = removeComma(propertyData.proposedMonthlyInsurance)
  const proposedMonthlyTaxes = removeComma(propertyData.proposedMonthlyTaxes)
  const proposedMonthlyHoaDues = removeComma(propertyData.proposedMonthlyHoaDues)
  const proposedMonthlyRent = removeComma(propertyData.proposedMonthlyRent)
  const floodInsurance = removeComma(propertyData.floodInsurance)
  const schoolTax = removeComma(propertyData.schoolTax)
  const otherTax = removeComma(propertyData.otherTax)
  let secondLienPayment = 0
  if (removeComma(propertyData.secondLien) > 0) secondLienPayment = removeComma(propertyData.secondLienPayment)

  let proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  if (
    [ProductTypes.FixAndFlip, ProductTypes.GroundUpConstruction, ProductTypes.Bridge].includes(
      productTypeForRules as ProductTypes,
    ) &&
    loan.interestType === 'Non Dutch'
  )
    proposedLoanAmount = removeComma(calcInitialLoanAmount(loan, true))

  const targetIndex = (loan?.multipleProperties?.findIndex((item) => item.id === propertyData.id) ?? -1) + 1
  const mortgage_payment = loan?.isMultipleProperties
    ? getAllocatedPrincipalAndInterest(loan, targetIndex, interest_rate, pricingType)
    : (proposedLoanAmount * interest_rate) / 100 / 12

  let Rlt: any = {
    title: 'Monthly Principal and Interest',
    mortgagePayment: getPrice2decimal(Number(mortgage_payment.toFixed(2))),
    proposedMonthlyInsurance: getPrice2decimal(proposedMonthlyInsurance),
    proposedMonthlyTaxes: getPrice2decimal(proposedMonthlyTaxes),
    proposedMonthlyHoaDues: getPrice2decimal(proposedMonthlyHoaDues),
    proposedMonthlyRent: getPrice2decimal(proposedMonthlyRent),
    secondLienPayment: getPrice2decimal(secondLienPayment),
    floodInsurance: getPrice2decimal(floodInsurance),
    schoolTax: getPrice2decimal(schoolTax),
    otherTax: getPrice2decimal(otherTax),
    rehabBudget: getPrice2decimal(rehabBudget),
  }

  const sum =
    removeComma(Rlt.mortgagePayment) +
    removeComma(Rlt.proposedMonthlyInsurance) +
    removeComma(Rlt.proposedMonthlyTaxes) +
    removeComma(Rlt.proposedMonthlyHoaDues) +
    removeComma(Rlt.secondLienPayment) +
    removeComma(Rlt.floodInsurance) +
    removeComma(Rlt.schoolTax) +
    removeComma(Rlt.otherTax)

  Rlt.sum = getPrice2decimal(Number(sum.toFixed(2)))

  const rtlTraderPercent = 0 / 100
  Rlt.requiredReserves = getPrice2decimal((Number(sum) * monthsReserve + rehabBudget * rtlTraderPercent).toFixed(2))

  let dscr: any = null
  if (interest_rate > 0) {
    dscr = removeComma(proposedMonthlyRent) / removeComma(sum)
    dscr = getPrice2decimal(dscr)
  }
  Rlt.dscr = dscr
  return Rlt
}

const getTotalCollateralValue = (data: IMultipleProperty[]) => {
  return data.map((item) => removeComma(item.asIsValue)).reduce((sum, item) => sum + item, 0)
}

const getAppraisedValueAllocedPercentage = (data: IMultipleProperty[], index: number) => {
  const asIsValue = removeComma(data[index]?.asIsValue ?? 0)
  if (!asIsValue) return 0
  const totalCollateralValue = getTotalCollateralValue(data)
  if (!totalCollateralValue) return 0
  let value = asIsValue / totalCollateralValue
  if (data.length - 1 == index) {
    let sum = 0
    data.forEach((_, vIndex) => {
      if (vIndex == index) return
      sum += getAppraisedValueAllocedPercentage(data, vIndex)
    })
    value = 1 - sum
  }
  return value
}

const getAllocatedPrincipalAndInterest = (loan: ILoan, index: number, interestRate: number, pricingType: any) => {
  const loanTerm = getLoanTerm(pricingType, loan.loanTerm)
  const productTypeForRules = getMappedProductType(loan.productType)
  let proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  if (
    [ProductTypes.FixAndFlip, ProductTypes.GroundUpConstruction, ProductTypes.Bridge].includes(
      productTypeForRules as ProductTypes,
    ) &&
    loan.interestType === 'Non Dutch'
  )
    proposedLoanAmount = removeComma(calcInitialLoanAmount(loan, true))

  const data = cloneDeep(loan?.multipleProperties ?? [])
  data.unshift({
    id: 0,
    subjectPropertyAddress: loan.subjectPropertyAddress,
    subjectPropertyAddressTBD: loan.subjectPropertyAddressTBD,
    transactionType: loan.transactionType,
    lienType: loan.lienType,
    proposedOccupancy: loan.proposedOccupancy,
    propertyHasBeenListed: loan.propertyHasBeenListed,
    propertyPurchasePrice: loan.propertyPurchasePrice,
    asIsValue: loan.asIsValue,
    afterRepairValue: loan.afterRepairValue,
    rehabBudget: loan.rehabBudget,
    constructionReserve: loan.constructionReserve,
    costsSpentToDate: loan.costsSpentToDate,
    secondLien: loan.secondLien,
    secondLienPayment: loan.secondLienPayment,
    lienPayoff: loan.lienPayoff,
    propertyType: loan.propertyType,
    condoType: loan.condoType,
    numberOfUnits: loan.numberOfUnits,
    vacant: loan.vacant,
    vacantIsPercentage: loan.vacantIsPercentage,
    proposedMonthlyRent: loan.proposedMonthlyRent,
    proposedMonthlyTaxes: loan.proposedMonthlyTaxes,
    proposedMonthlyInsurance: loan.proposedMonthlyInsurance,
    proposedMonthlyHoaDues: loan.proposedMonthlyHoaDues,
    floodInsurance: loan.floodInsurance,
    schoolTax: loan.schoolTax,
    otherTax: loan.otherTax,
  })

  const appraisedValueAllocatedPercentage = getAppraisedValueAllocedPercentage(data, index)
  const paymentPrincipalAndInterest = PMT(interestRate / 100 / 12, loanTerm, proposedLoanAmount, 0, 0) * -1
  return appraisedValueAllocatedPercentage * paymentPrincipalAndInterest
}

export function PMT(ir: number, np: number, pv: number, fv: number, type: number) {
  /*
   * ir   - interest rate per month
   * np   - number of periods (months)
   * pv   - present value
   * fv   - future value
   * type - when the payments are due:
   *        0: end of the period, e.g. end of month (default)
   *        1: beginning of period
   */
  var pmt, pvif

  fv || (fv = 0)
  type || (type = 0)

  if (ir === 0) return -(pv + fv) / np

  pvif = Math.pow(1 + ir, np)
  pmt = (-ir * (pv * pvif + fv)) / (pvif - 1)

  if (type === 1) pmt /= 1 + ir

  return pmt
}
